"use client";

import React from "react";
import { CircleCheck, Search, ShieldCheck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function SearchForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-secondary rounded-full flex items-center justify-center text-white mb-2">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Search Initiated!</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Start Another Search
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" className="bg-background" {...field} />
            </FormControl>
            <FormDescription>Enter the name of the primary contact.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="property_address" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default">Property Address (if known)</FormLabel>
            <FormControl>
              <Input placeholder="123 Main St, City, State" className="bg-background" {...field} />
            </FormControl>
            <FormDescription>Provide the address of the property related to the unclaimed funds, if you have it.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^\+?[0-9\s\-\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="btn-primary w-full mt-2 text-lg h-12">
        {isSubmitting ? <span className="flex items-center gap-2">
            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span> : <span className="flex items-center gap-2">
            <Search className="size-5" />
            Check for Funds
          </span>}
      </Button>
      <p className="text-xs text-center text-muted-foreground mt-4 flex items-center justify-center gap-1">
        <ShieldCheck className="size-3" /> Your information is secure and confidential.
      </p>
    </Form>;
}
export default function FundSearch() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const benefits = [{
    icon: <Search className="size-6 text-primary" />,
    title: "Comprehensive Database Scan",
    description: "We cross-reference multiple state and county records to locate any funds tied to your name or property."
  }, {
    icon: <Clock className="size-6 text-primary" />,
    title: "Quick Initial Assessment",
    description: "Our initial search is fast, providing you with preliminary results so you know where you stand without delay."
  }, {
    icon: <ShieldCheck className="size-6 text-primary" />,
    title: "No Upfront Costs",
    description: "You pay nothing out of pocket. We only earn a fee if we successfully recover your unclaimed funds."
  }];
  return <section id="fund-search" className="bg-background py-16 md:py-24 relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -right-[5%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[100px]" />
        <div className="absolute -bottom-[10%] -left-[5%] w-[40%] h-[40%] rounded-full bg-secondary/5 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary px-3 py-1 text-sm font-medium text-white">
                <span className="flex h-2 w-2 rounded-full bg-primary mr-2"></span>
                Free Initial Consultation
              </div>
              <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tight text-foreground">
                Start Your Free Fund Search Today
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans">
                Millions of dollars in surplus funds from tax deed sales and foreclosures go unclaimed every year. RR Recovery Solution is dedicated to reuniting you with what is rightfully yours. Fill out the form to begin your risk-free search.
              </p>
            </div>

            <div className="space-y-6 pt-4">
              {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex gap-4">
                  <div className="flex-shrink-0 mt-1 size-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    {benefit.icon}
                  </div>
                  <div>
                    <h4 className="text-xl font-default font-semibold text-foreground mb-1">{benefit.title}</h4>
                    <p className="text-muted-foreground font-sans">{benefit.description}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className="relative">
            {}
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-secondary/20 rounded-2xl transform rotate-2 scale-105 -z-10 blur-sm"></div>
            
            <Card className="card border-0 shadow-strong bg-background/95 backdrop-blur-sm relative z-10">
              <CardHeader className="pb-4 border-b border-border/50 mb-6">
                <CardTitle className="text-2xl font-default text-center">Free Fund Search</CardTitle>
                <CardDescription className="text-muted-foreground text-center text-base !font-bold">Enter your details below and our team will begin investigating immediately.</CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="free_fund_search_form_home" sectionName="FundSearch" defaultValues={{
                name: "",
                property_address: "",
                phone: "",
                email: ""
              }} validationRules={{
                name: {
                  required: true
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                email: {
                  required: true,
                  pattern: "email"
                }
              }} submitText="Check for Funds" successMessage="Thank you! Your free fund search request has been received. Our team will review the information and contact you shortly with our preliminary findings." errorMessage="There was a problem submitting your request. Please check your information and try again.">
                  <SearchForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}